// src/theme/styles.js
// Global stiller, @keyframes animasyonları vb.

// Faz 1 · Motion CSS variable'lari merkezi tek kaynaktan (tokens/motion.js)
// :root'a enjekte edilir. Buradaki dupe tanimlar kaldirildi.
import { motionCssVars } from "./tokens/motion";

const styles = {
  global: (props) => ({
    ":root": {
      ...motionCssVars,
    },
    // -------------------------------------------------------
    // 1) HTML ve Body Temel Stilleri
    // -------------------------------------------------------
    "html, body": {
      backgroundColor: "var(--chakra-colors-pageBackground)", // semantic token
      color: "var(--chakra-colors-textPrimary)",               // semantic token
      fontFamily: "body", // typography.js'den
      fontSize: { base: "15px", md: "16px" }, // Mobil için 15px, desktop için 16px
      lineHeight: "base", // typography.js'den (veya 'tall' gibi daha ferah bir değer)
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
      transitionProperty: "background-color, color",
      transitionDuration: "var(--motion-duration-fast)",
      transitionTimingFunction: "var(--motion-ease-apple)",
      // Faz 4.3++ v2 · scroll-behavior: auto global, hicbir cihazda smooth yok.
      // Desktop trackpad'de bile smooth-scroll native momentum ile catisip
      // takilma hissi yaratiyor. Anchor link'lerin smooth animasyonu gerekirse
      // JS ile programatik smooth scrollTo kullanilir.
      scrollBehavior: "auto",
      // PWA Dalga 1 · Pull-to-refresh ve overscroll bounce'u devre disi birak
      // (standalone PWA modunda yanlislikla refresh'i ve elastic scroll'u onler).
      // Container'lar ic scrollunu yine yapabilir.
      overscrollBehaviorY: "contain",
      overscrollBehaviorX: "none",
      // NOT: touch-action: manipulation body uzerinde KALDIRILDI cunku
      // bazi desktop tarayicilarinda trackpad wheel event'ini gesture olarak
      // yorumlayip scroll'u bloke edebiliyor. Artik sadece Button component
      // baseStyle'inda uygulaniyor (double-tap zoom icin yeterli; viewport
      // meta + main.jsx gesture listener diger zoom yollarini kilitliyor).
    },
    // PWA Dalga 1 · Standalone (installed PWA) mode'a ozel davranislar.
    // Tarayicida acikken normal, installed PWA'da daha "app-like".
    "@media (display-mode: standalone), (display-mode: window-controls-overlay), (display-mode: minimal-ui)": {
      "html, body": {
        // Standalone'da tum overscroll'lari sertce engelle
        overscrollBehavior: "none",
        // iOS PWA'da tap-highlight tamamen sifirlansin (button'da zaten var, body'de yedek)
        WebkitTapHighlightColor: "transparent",
      },
      // Text selection: yalniz nav/header/aside/footer/dock'larda kapali; icerik bolgeleri serbest.
      // Eskiden body uzerinde global user-select:none vardi · ders metni, soru sikki, bildirim vb.
      // kopyalanamiyordu (bug raporu yazma deneyimini de kiriyordu).
      "nav, header, aside, footer, [data-no-select='true']": {
        WebkitUserSelect: "none",
        userSelect: "none",
        // PWA Dalga 3 · iOS long-press menu (Copy/Share) sadece nav/chrome'da kapali
        // icerik bolgelerinde aktif kalsin · ders metni vs. uzerine basili tutarak
        // kopyalama davranisi korunmali
        WebkitTouchCallout: "none",
      },
      "input, textarea, [contenteditable='true'], .selectable, .ders-container, .reading-mode, main, p, span, h1, h2, h3, h4, h5, h6, li, td, th, .lecture-section, .question-card, .flashcard-content, .algospot-notes": {
        WebkitUserSelect: "text",
        userSelect: "text",
      },
    },
    body: {
      minHeight: "var(--app-height)", // Sayfanın her zaman tüm yüksekliği kaplaması için
      // Faz 4.3++ · 'hidden' yerine 'clip': 'hidden' overflowY'yi implicitly auto'ya
      // zorlayip body'yi gizli scroll container yapiyordu -> desktop wheel body
      // icinde bloke oluyordu. 'clip' ayni gorsel sonuc + scroll container yaratmaz.
      overflowX: "clip", // Yatay scrollbar'ı önle, scroll-container yaratmaz
      position: "relative",
      // Faz 4.3++ · Body bg gradient KALDIRILDI ve pseudo-element'e tasindi (asagida).
      // Eskiden body uzerinde radial-gradient + scroll, her scroll frame'inde GPU
      // repaint tetikliyordu. Yeni yapi: position:fixed pseudo-element -> 1x boya,
      // scroll'da repaint yok. Gorsel sonuc birebir ayni.
    },
    // Faz 4.3++ · Sabit arka plan dekoratif gradient (scroll'da repaint olmaz)
    "body::before": {
      content: '""',
      position: "fixed",
      inset: 0,
      zIndex: -1,
      pointerEvents: "none",
      backgroundImage: props.colorMode === "dark"
        ? `radial-gradient(1200px 700px at 0% -10%, ${props.theme.colors.brand['300-alpha-30']}, transparent 60%),
           radial-gradient(900px 600px at 100% 0%, ${props.theme.colors.brand['500-alpha-25']}, transparent 55%)`
        : `radial-gradient(1200px 700px at 0% -10%, ${props.theme.colors.brand['500-alpha-20']}, transparent 60%),
           radial-gradient(900px 600px at 100% 0%, ${props.theme.colors.yellow['400_alpha_10']}, transparent 55%)`,
      backgroundRepeat: "no-repeat",
      willChange: "auto",
    },

    // -------------------------------------------------------
    // 2) Odak / Focus Stilleri
    // Mouse tikla → ring yok. Klavye navigasyonu → ring.
    // Interactive elemanlar icin WCAG AA ring tanimi.
    // -------------------------------------------------------
    "*:focus:not(:focus-visible)": {
      boxShadow: "none",
      outline: "none",
    },
    "a:focus-visible, button:focus-visible, [role='button']:focus-visible, [tabindex]:focus-visible": {
      outline: "none",
      boxShadow: props.colorMode === 'dark'
        ? "0 0 0 3px var(--chakra-colors-brand-300_alpha_40)"
        : "0 0 0 3px var(--chakra-colors-brand-500_alpha_40)",
      borderRadius: "inherit",
    },

    // -------------------------------------------------------
    // 3) Selection (metin seçimi rengi · brand alpha)
    // -------------------------------------------------------
    "::selection": {
      background: props.colorMode === 'dark'
        ? "var(--chakra-colors-brand-300_alpha_30)"
        : "var(--chakra-colors-brand-500_alpha_20)",
      color: props.colorMode === 'dark' ? "white" : "inherit",
    },

    // -------------------------------------------------------
    // 4) Scrollbar · Linear / Notion tarzi, non-intrusive.
    //    Track transparan, thumb subtle neutral, hover'da koyulasir.
    // -------------------------------------------------------
    "::-webkit-scrollbar": {
      width: "10px",
      height: "10px",
    },
    "::-webkit-scrollbar-track": {
      background: "transparent",
    },
    "::-webkit-scrollbar-thumb": {
      background: props.colorMode === 'dark'
        ? "rgba(255, 255, 255, 0.10)"
        : "rgba(15, 20, 25, 0.14)",
      borderRadius: "8px",
      border: "2px solid transparent",
      backgroundClip: "padding-box",
      transition: "background-color var(--motion-duration-fast) var(--motion-ease-apple)",
    },
    "::-webkit-scrollbar-thumb:hover": {
      background: props.colorMode === 'dark'
        ? "rgba(255, 255, 255, 0.20)"
        : "rgba(15, 20, 25, 0.28)",
      backgroundClip: "padding-box",
      border: "2px solid transparent",
    },
    "::-webkit-scrollbar-corner": {
      background: "transparent",
    },
    // Firefox scrollbar
    html: {
      scrollbarColor: props.colorMode === 'dark'
        ? "rgba(255,255,255,0.18) transparent"
        : "rgba(15,20,25,0.20) transparent",
      scrollbarWidth: "thin",
    },

    // -------------------------------------------------------
    // 5) Reduced Motion · kullanıcı tercihine saygı
    // -------------------------------------------------------
    "@media (prefers-reduced-motion: reduce)": {
      "*, *::before, *::after": {
        animationDuration: "0.01ms !important",
        animationIterationCount: "1 !important",
        transitionDuration: "0.01ms !important",
        scrollBehavior: "auto !important",
      },
    },

    // -------------------------------------------------------
    // 6) @keyframes Animasyonları
    // -------------------------------------------------------
    "@keyframes pulse": {
      "0%, 100%": { transform: "scale(1)", opacity: "0.75" },
      "50%": { transform: "scale(1.05)", opacity: "1" },
    },
    "@keyframes subtlePulse": { // Daha ince bir pulse
      "0%, 100%": { opacity: "0.8" },
      "50%": { opacity: "1" },
    },
    "@keyframes futuristicGlow": { // Örnek bir glow animasyonu
      "0%": {
        boxShadow: `0 0 5px 0px ${props.theme.colors.brand[props.colorMode === 'dark' ? 300 : 500]}33`
      },
      "50%": {
        boxShadow: `0 0 15px 3px ${props.theme.colors.brand[props.colorMode === 'dark' ? 300 : 500]}66`
      },
      "100%": {
        boxShadow: `0 0 5px 0px ${props.theme.colors.brand[props.colorMode === 'dark' ? 300 : 500]}33`
      },
    },

    // -------------------------------------------------------
    // 7) "Ders Kitabı" Konteyneri
    // -------------------------------------------------------
    // Tüm konu anlatımı / makale içeriği bu sınıf altına sarılacak.
".ders-container": {
  /* 100 % genişle, üst Container ne kadarsa onu kullan */
  w: "100%",
  maxW: "100%",
  /* merkezlemek için artık mx:auto gerekmez */
  my: ["2rem", "2.5rem", "3rem"],   // sadece dikey boşluk
  p: ["1.75rem", "2rem", "2.5rem"], // responsive iç padding

  /* KART TASARIMI · Faz 4.1: hex'ler reading.* token'larina migre edildi */
  bg: "var(--chakra-colors-reading-cardBg)",
  border: "1px solid",
  borderColor: "var(--chakra-colors-reading-border)",
  borderRadius: "md",
  boxShadow: "var(--chakra-shadows-elevation-2)",

  position: "relative",
  overflow: "hidden",

  /* Radial glow */
  _before: {
    content: `""`,
    position: "absolute",
    top: "-60px",
    right: "-60px",
    width: "220px",
    height: "220px",
    bg: `radial-gradient(circle at center, ${
      props.theme.colors.brand[props.colorMode === "dark" ? 300 : 500]
    }33, transparent 60%)`,
    filter: "blur(45px)",
    zIndex: 0,
  },

  /* Çocuklar üst layer’da */
  "& > *": { position: "relative", zIndex: 1 },
},

    // -------------------------------------------------------
    // 8) Başlık Stilleri (Heading)
    // -------------------------------------------------------

    // Ana başlık - h1 · Faz 4.1: hex'ler reading.heading + reading.border'a tasindi
    ".ders-container h1": {
      fontSize: ["2.25rem", "2.5rem", "3rem"], // Responsive
      fontWeight: "extrabold",
      textAlign: "center",
      textTransform: "uppercase",
      letterSpacing: "widest",
      color: "var(--chakra-colors-reading-heading)",
      my: "2rem",
      pb: "0.5rem",
      borderBottom: "2px solid",
      borderColor: "var(--chakra-colors-reading-border)",
      // Faz 4.3++ · Sonsuz pulse animasyonu kaldirildi (sayfa scroll'da bile
      // GPU paint cycle calistirir; estetik katki minimal, perf kazanim onemli).
    },
    // İkincil başlık - h2 · Faz 4.1: reading.subheading + reading.accent
    ".ders-container h2": {
      fontSize: ["1.5rem", "1.75rem", "2rem"],
      fontWeight: "bold",
      color: "var(--chakra-colors-reading-subheading)",
      borderLeft: "4px solid",
      borderColor: "var(--chakra-colors-reading-accent)",
      pl: "1rem",
      ml: "-1rem",
      mt: "2rem",
      mb: "1rem",
      // Faz 4.3++ · Hover'da sonsuz glow animasyonu kaldirildi
      // (mouse uzun sure h2 uzerindeyken sayfanin scroll perf'i bozuyordu)
      _hover: {
        boxShadow: "0 0 0 3px var(--chakra-colors-brand-500_alpha_15)",
        transition: "box-shadow var(--motion-duration-base) var(--motion-ease-apple)",
      },
    },
    // Alt başlık - h3 · Faz 4.1: reading.subheading + reading.accent
    ".ders-container h3": {
      fontSize: ["1.25rem", "1.5rem", "1.75rem"],
      fontWeight: "semibold",
      color: "var(--chakra-colors-reading-subheading)",
      mt: "1.5rem",
      mb: "0.75rem",
      position: "relative",
      _before: {
        content: `""`,
        position: "absolute",
        left: 0,
        top: 0,
        width: "25px",
        height: "4px",
        bg: "var(--chakra-colors-reading-accent)",
        borderRadius: "md",
      },
      pl: "1.5rem",
    },
    // Daha küçük başlık - h4 · Faz 4.1: reading.subheading
    ".ders-container h4": {
      fontSize: ["1.1rem", "1.25rem", "1.5rem"],
      fontWeight: "medium",
      color: "var(--chakra-colors-reading-subheading)",
      mt: "1.25rem",
      mb: "0.5rem",
    },

    // -------------------------------------------------------
    // 9) Paragraf (p) Stilleri · Faz 4.1: reading.text token
    // -------------------------------------------------------
    ".ders-container p": {
      mb: "1rem",
      textAlign: "justify",
      fontSize: ["md", "lg"],
      color: "var(--chakra-colors-reading-text)",
      lineHeight: "tall",
      _first: { // İlk paragraf başının büyük fontu
        fontSize: ["lg", "xl"],
        fontWeight: "semibold",
      },
    },

    // -------------------------------------------------------
    // 10) Dropcap (Büyük Harf) Sınıfı · Faz 4.1
    // -------------------------------------------------------
    ".ders-container .dropcap": {
      float: "left",
      fontSize: ["3rem", "3.5rem", "4rem"],
      lineHeight: 1,
      pr: "8px",
      pt: "4px",
      color: "var(--chakra-colors-reading-accent)",
      fontWeight: "700",
      fontFamily: "heading",
      textTransform: "uppercase",
      // Faz 4.3++ · Dropcap'taki sonsuz pulse kaldirildi (perf)
    },

    // -------------------------------------------------------
    // 9) Blockquote Stili · Faz 4.1: reading.quote.* token'lari
    // -------------------------------------------------------
    ".ders-container blockquote": {
      my: "1.5rem",
      mx: ["1rem", "2rem"],
      px: "1rem",
      py: "0.75rem",
      borderLeft: "4px solid",
      borderColor: "var(--chakra-colors-reading-quote-border)",
      bg: "var(--chakra-colors-reading-quote-bg)",
      fontStyle: "italic",
      color: "var(--chakra-colors-reading-quote-text)",
      position: "relative",
      _before: {
        content: `""`,
        position: "absolute",
        left: "-10px",
        top: "10px",
        fontSize: "3rem",
        color: "var(--chakra-colors-reading-quote-border)",
        lineHeight: 1,
        opacity: 0.2,
        transform: "rotate(180deg)",
      },
    },

    // -------------------------------------------------------
    // 10) Liste (ul, ol) Stilleri · Faz 4.1: reading.text
    // -------------------------------------------------------
    ".ders-container ul, .ders-container ol": {
      my: "1rem",
      mx: ["1rem", "2rem"],
      pl: ["1.5rem", "2rem"],
      color: "var(--chakra-colors-reading-text)",
    },
    ".ders-container ul": {
      listStyleType: "disc",
    },
    ".ders-container ol": {
      listStyleType: "decimal",
    },
    ".ders-container ul li, .ders-container ol li": {
      mb: "0.5rem",
      pl: "0.5rem",
      textAlign: "justify",
    },

    // -------------------------------------------------------
    // 11) Tablo Stili
    // -------------------------------------------------------
    // Faz 4.1 · reading.table.* token'larina migre edildi
    ".ders-container table": {
      width: "100%",
      borderCollapse: "collapse",
      my: "1.5rem",
      bg: "transparent",
      fontSize: ["sm", "md"],
    },
    ".ders-container th, .ders-container td": {
      border: "1px solid",
      borderColor: "var(--chakra-colors-reading-table-border)",
      px: "0.75rem",
      py: "0.5rem",
      textAlign: "left",
    },
    ".ders-container th": {
      bg: "var(--chakra-colors-reading-table-headerBg)",
      fontWeight: "600",
      color: "var(--chakra-colors-reading-table-headerText)",
    },
    ".ders-container tr:nth-of-type(even)": {
      bg: "var(--chakra-colors-reading-table-rowAltBg)",
    },

    // -------------------------------------------------------
    // 12) Resim / Figure Stilleri
    // -------------------------------------------------------
    ".ders-container .figure": {
      my: "2rem",
      textAlign: "center",
    },
    // Faz 4.1 · reading.border + elevation.* token'larina migre edildi
    ".ders-container .figure img": {
      maxW: "100%",
      border: "1px solid",
      borderColor: "var(--chakra-colors-reading-border)",
      borderRadius: "md",
      boxShadow: "var(--chakra-shadows-elevation-2)",
      transition: "transform var(--motion-duration-base) var(--motion-ease-apple), box-shadow var(--motion-duration-base) var(--motion-ease-apple)",
      _hover: {
        transform: "scale(1.02)",
        boxShadow: "var(--chakra-shadows-elevation-3)",
      },
    },
    ".ders-container .figure figcaption": {
      fontSize: "0.9rem",
      color: "var(--chakra-colors-reading-textMuted)",
      mt: "0.5rem",
    },

    // -------------------------------------------------------
    // 13) Kod Bloğu ve Inline Kod Stilleri
    // -------------------------------------------------------
    // Faz 4.1 · reading.code.* token'larina migre edildi
    ".ders-container pre": {
      bg: "var(--chakra-colors-reading-code-blockBg)",
      borderRadius: "md",
      p: "1rem",
      overflowX: "auto",
      fontSize: ["sm", "md"],
      lineHeight: "shorter",
      fontFamily: "mono",
      position: "relative",
      border: "1px solid",
      borderColor: "var(--chakra-colors-reading-code-blockBorder)",
      boxShadow: "var(--chakra-shadows-elevation-2)",
      color: "var(--chakra-colors-reading-code-blockText)",
      _before: {
        content: `""`,
        position: "absolute",
        top: "8px",
        left: "8px",
        width: "12px",
        height: "12px",
        bg: "var(--chakra-colors-rose-500)",
        borderRadius: "50%",
        boxShadow: `0 0 4px var(--chakra-colors-reading-accent)`,
        // Faz 4.3++ · Code blogundaki kirmizi noktanin sonsuz pulse'u kaldirildi
      },
    },
    ".ders-container code": {
      bg: "var(--chakra-colors-reading-code-inlineBg)",
      px: "0.25rem",
      py: "0.125rem",
      borderRadius: "sm",
      fontSize: "0.9rem",
      fontFamily: "mono",
      color: "var(--chakra-colors-reading-code-inlineText)",
    },

    // -------------------------------------------------------
    // 14) Vurgulu Metin (Mark) Stili
    // -------------------------------------------------------
    // Faz 4.1 · reading.highlight.* token'larina migre edildi
    ".ders-container mark": {
      bg: "var(--chakra-colors-reading-highlight-yellow)",
      color: "var(--chakra-colors-reading-highlight-text)",
      px: "0.2rem",
      py: "0.1rem",
      borderRadius: "sm",
      fontWeight: "semibold",
    },

    // -------------------------------------------------------
    // 15) Dipnot / Footnote Stili · Faz 4.1
    // -------------------------------------------------------
    ".ders-container .footnote": {
      fontSize: "0.8rem",
      color: "var(--chakra-colors-reading-textMuted)",
      mt: "2rem",
      borderTop: "1px solid",
      borderColor: "var(--chakra-colors-reading-border)",
      pt: "1rem",
    },
    ".ders-container .footnote sup": {
      fontSize: "0.7rem",
      verticalAlign: "super",
      color: "var(--chakra-colors-reading-textSecondary)",
    },

    // -------------------------------------------------------
    // 16) Alt Çizili Metin (Underline) Sınıfı · Faz 4.1
    // -------------------------------------------------------
    ".ders-container .underline": {
      textDecoration: "underline",
      textDecorationColor: "var(--chakra-colors-reading-accent)",
      textUnderlineOffset: "3px",
    },

    // -------------------------------------------------------
    // 17) Link (a) Stilleri - AI Hissi Veren Glow/Efekt
    // -------------------------------------------------------
    // Faz 4.1 · reading.link / linkHover / accent token'larina migre edildi
    ".ders-container a": {
      color: "var(--chakra-colors-reading-link)",
      textDecoration: "none",
      position: "relative",
      transition: "color var(--motion-duration-fast) var(--motion-ease-apple), text-shadow var(--motion-duration-fast) var(--motion-ease-apple)",
      _hover: {
        color: "var(--chakra-colors-reading-linkHover)",
        textShadow: `0 0 6px var(--chakra-colors-reading-linkUnderline)`,
        _after: {
          transform: "scaleX(1)",
        },
      },
      _after: {
        content: `""`,
        position: "absolute",
        left: 0,
        bottom: "-2px",
        width: "100%",
        height: "2px",
        bg: "var(--chakra-colors-reading-accent)",
        transform: "scaleX(0)",
        transformOrigin: "left",
        transition: "transform var(--motion-duration-base) var(--motion-ease-apple)",
      },
    },

    // -------------------------------------------------------
    // 18) Satır İçi Resim (Inline Image) Stilleri
    // -------------------------------------------------------
    // Faz 4.1 · elevation.* shadow token'larina migre edildi
    ".ders-container img.inline": {
      maxW: ["50%", "60%", "70%"],
      display: "block",
      mx: "auto",
      my: "1.5rem",
      borderRadius: "md",
      boxShadow: "var(--chakra-shadows-elevation-2)",
      transition: "transform var(--motion-duration-base) var(--motion-ease-apple), box-shadow var(--motion-duration-base) var(--motion-ease-apple)",
      _hover: {
        transform: "scale(1.03)",
        boxShadow: "var(--chakra-shadows-elevation-3)",
      },
    },

    // -------------------------------------------------------
    // 19) Tablo Hücre Hover Efekti · Faz 4.3++ (sade, scroll dostu)
    // -------------------------------------------------------
    // hover sadece pointer:fine cihazlarda (mouse) · touchta scroll kaymalarini
    // hover gibi yorumlamayi engeller, mobilde gereksiz paint cycle yok.
    "@media (hover: hover) and (pointer: fine)": {
      ".ders-container tr:hover td": {
        bg: "var(--chakra-colors-reading-table-rowHoverBg)",
        transition: "background-color var(--motion-duration-fast) var(--motion-ease-apple)",
      },
      // List item hover · pl kaymasi kaldirildi (layout shift = scroll thrash).
      // Sadece bg degisimi.
      ".ders-container ul li:hover, .ders-container ol li:hover": {
        bg: "var(--chakra-colors-reading-bgSubtle)",
        borderRadius: "md",
        transition: "background-color var(--motion-duration-fast) var(--motion-ease-apple)",
      },
    },
    "@media (prefers-reduced-motion: reduce)": {
      "html, body": {
        scrollBehavior: "auto",
      },
      "*, *::before, *::after": {
        animationDuration: "0.01ms",
        animationIterationCount: 1,
        transitionDuration: "0.01ms",
      },
    },

    // -------------------------------------------------------
    // 21) Responsive Farklılaştırmalar
    // Faz 1 · Hand-rolled '768px' yerine Chakra theme breakpoint'i.
    // breakpoints.md = '48em' (768px); below-md max-width = (md - 0.01em).
    // -------------------------------------------------------
    [`@media (max-width: calc(${props.theme.breakpoints.md} - 0.01em))`]: {
      ".ders-container": {
        p: "1.5rem",
        my: "2rem",
      },
      ".ders-container h1": {
        fontSize: "2rem",
      },
      ".ders-container h2": {
        fontSize: "1.5rem",
      },
      ".ders-container pre": {
        fontSize: "0.85rem",
      },
    },
  }),
};

export default styles;
